import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListItem,
  ListItemText,
  Tooltip,
  Typography,
} from '@mui/material';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import { useDispatch, useSelector } from 'react-redux';
import {
  fetchNurturingPlanVersions,
  launchDealerForNurturingPlan,
} from 'app/apiService/nurturingPlan/nurturingPlanSettingsDataService';
import { headerActions } from 'app/pages/Header/slice';
import { selectDealerSelected } from 'app/pages/Header/selectors';
import { notificationAlertActions } from 'app/pages/NotificationAlert/slice';
import { DealerLaunchMaterializationStatus } from 'app/components/ReusableUIComponents/LowLevelComponents/DealerLaunchMaterializationStatus';
import {
  mergeDealerWithLaunchAck,
  persistSelectedDealerToLocalStorage,
  shouldShowDealerLaunchMaterializationStatus,
} from 'utils/dealerLaunchMaterialization';
import { useDealerLaunchMaterializationStatusClick } from 'app/pages/Header/hooks/useDealerLaunchMaterializationStatusClick';
import { parseCalendarDateFromApi } from 'utils/dateUtils';

interface OnboardingLaunchDealerButtonProps {
  dealerId: string;
}

const resolveLaunchDateRaw = (dealer: unknown): string | null => {
  if (typeof dealer !== 'object' || dealer === null) return null;
  const raw = (dealer as { launch_date?: unknown })?.launch_date;
  if (raw == null || raw === '') return null;
  if (typeof raw === 'string') {
    const trimmed = raw.trim();
    return trimmed || null;
  }
  if (raw instanceof Date) return raw.toISOString();
  return String(raw);
};

const LAUNCH_CONFIRM_BULLETS = [
  'Launching the dealer is a long-running process and may take several hours to complete.',
  'Validates dealer settings are configured correctly before launch begins.',
  'Validates the warm-up schedule and materializes sending calendars for nurturing channels.',
];

const OnboardingLaunchDealerButton: React.FC<OnboardingLaunchDealerButtonProps> = ({
  dealerId,
}) => {
  const dispatch = useDispatch();
  const selectedDealer = useSelector(selectDealerSelected);
  const [launchConfirmOpen, setLaunchConfirmOpen] = useState(false);
  const [launching, setLaunching] = useState(false);
  const [hasPublishedVersion, setHasPublishedVersion] = useState(false);
  const [versionsLoading, setVersionsLoading] = useState(false);

  const launchDateRaw = resolveLaunchDateRaw(selectedDealer);
  const parsedLaunchDate = launchDateRaw
    ? parseCalendarDateFromApi(launchDateRaw)
    : null;
  const hasLaunchDate = Boolean(parsedLaunchDate?.isValid);
  const showLaunchMaterializationStatus =
    shouldShowDealerLaunchMaterializationStatus(selectedDealer);
  const { onStatusClick } = useDealerLaunchMaterializationStatusClick(selectedDealer);

  useEffect(() => {
    let cancelled = false;

    const loadVersions = async () => {
      if (
        !dealerId ||
        hasLaunchDate ||
        showLaunchMaterializationStatus
      ) {
        setHasPublishedVersion(false);
        return;
      }

      setVersionsLoading(true);
      try {
        const versions = await fetchNurturingPlanVersions({ dealerId });
        if (!cancelled) {
          setHasPublishedVersion(
            versions.some((version) => version.nurturing_plan_status === 'PUBLISHED'),
          );
        }
      } catch {
        if (!cancelled) {
          setHasPublishedVersion(false);
        }
      } finally {
        if (!cancelled) {
          setVersionsLoading(false);
        }
      }
    };

    void loadVersions();

    return () => {
      cancelled = true;
    };
  }, [dealerId, hasLaunchDate, showLaunchMaterializationStatus]);

  const launchDisabledReason = useMemo(() => {
    if (showLaunchMaterializationStatus) {
      return 'Dealership launch is already in progress or pending';
    }
    if (hasLaunchDate) return 'Dealership is already launched';
    if (versionsLoading) return 'Checking nurturing plan status';
    if (!hasPublishedVersion) return 'Publish a nurturing plan before launching';
    return 'Launch dealership';
  }, [hasLaunchDate, hasPublishedVersion, showLaunchMaterializationStatus, versionsLoading]);

  const handleLaunch = useCallback(async () => {
    if (
      !dealerId ||
      launching ||
      hasLaunchDate ||
      showLaunchMaterializationStatus ||
      !hasPublishedVersion
    ) {
      return;
    }

    setLaunching(true);
    try {
      const response = await launchDealerForNurturingPlan({ dealerId });
      const statusText: string | undefined = response?.statusText;

      if (statusText && statusText !== 'success') {
        throw new Error(response?.message || 'Failed to launch dealership');
      }

      const launchAck = response?.body ?? response?.data;
      const nextSelectedDealer = mergeDealerWithLaunchAck(selectedDealer, launchAck);
      if (nextSelectedDealer) {
        dispatch(headerActions.selectDealer(nextSelectedDealer));
        persistSelectedDealerToLocalStorage(nextSelectedDealer);
      }

      dispatch(
        notificationAlertActions.addNotification({
          open: true,
          severity: 'success',
          message:
            response?.message ||
            'Dealer launch accepted. Calendar materialization is running in the background.',
        }),
      );

      setLaunchConfirmOpen(false);
    } catch (err: unknown) {
      const message =
        err instanceof Error ? err.message : 'Failed to launch dealership';
      dispatch(
        notificationAlertActions.addNotification({
          open: true,
          severity: 'error',
          message,
        }),
      );
    } finally {
      setLaunching(false);
    }
  }, [
    dealerId,
    dispatch,
    hasLaunchDate,
    hasPublishedVersion,
    showLaunchMaterializationStatus,
    launching,
    selectedDealer,
  ]);

  if (hasLaunchDate) {
    return null;
  }

  if (showLaunchMaterializationStatus) {
    return (
      <DealerLaunchMaterializationStatus
        dealer={selectedDealer}
        variant="header"
        onClick={onStatusClick}
      />
    );
  }

  return (
    <>
      <Tooltip title={launchDisabledReason}>
        <span>
          <Button
            variant="contained"
            size="small"
            color="primary"
            startIcon={<RocketLaunchIcon fontSize="small" />}
            onClick={() => setLaunchConfirmOpen(true)}
            disabled={versionsLoading || !hasPublishedVersion}
            sx={{
              whiteSpace: 'nowrap',
              height: 40,
              textTransform: 'none',
              fontWeight: 600,
              letterSpacing: 0,
              boxShadow: 'none',
            }}
          >
            Launch Dealer
          </Button>
        </span>
      </Tooltip>

      <Dialog
        open={launchConfirmOpen}
        onClose={() => {
          if (!launching) {
            setLaunchConfirmOpen(false);
          }
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Launch Dealer?</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Please review the following before proceeding:
          </Typography>
          <List dense disablePadding sx={{ listStyleType: 'disc', pl: 2.5 }}>
            {LAUNCH_CONFIRM_BULLETS.map((bullet) => (
              <ListItem
                key={bullet}
                disableGutters
                sx={{ display: 'list-item', py: 0.35, pl: 0 }}
              >
                <ListItemText
                  primary={bullet}
                  primaryTypographyProps={{ variant: 'body2', color: 'text.primary' }}
                />
              </ListItem>
            ))}
          </List>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button
            onClick={() => setLaunchConfirmOpen(false)}
            variant="outlined"
            size="small"
            disabled={launching}
          >
            Cancel
          </Button>
          <Button
            onClick={() => {
              void handleLaunch();
            }}
            variant="contained"
            color="primary"
            size="small"
            disabled={launching || !hasPublishedVersion}
            startIcon={
              launching ? <CircularProgress size={16} color="inherit" /> : undefined
            }
          >
            {launching ? 'Proceeding...' : 'Proceed'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default OnboardingLaunchDealerButton;
